import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "@material/web/textfield/outlined-text-field.js";
import "@material/web/select/outlined-select.js";
import "@material/web/select/select-option.js";
import "@material/web/button/filled-button.js";
import "@material/web/button/outlined-button.js";
import "@material/web/button/text-button.js";
import "@material/web/icon/icon.js";
import "@material/web/progress/circular-progress.js";

import "../components/app-shell";
import { sharedStyles } from "../styles/shared";
import { inputValue } from "../lib/dom";
import { navigate } from "../lib/navigation";
import { toast } from "../lib/toast";
import { companies, type Company, type Contact } from "../lib/companies";
import { provisioning, type Prospect, type GiGood } from "../lib/provisioning";
import { ApiError } from "../lib/api";

const STAGE_LABEL: Record<string, string> = {
  candidate: "Candidate",
  contacted: "Contacted",
  interested: "Interested",
  tenant_created: "Tenant created",
  ebsi_onboarding: "EBSI onboarding",
  active: "Live tenant",
  declined: "Declined",
};

const STATUSES = ["new", "contacted", "interested", "customer", "declined"];

// scalar fields rendered as editable text inputs: [key, label]
const FIELDS: Array<[keyof Company, string]> = [
  ["name", "Name"],
  ["legal_name", "Legal name"],
  ["registry_id", "Registry id (SIREN/VAT)"],
  ["industry", "Industry"],
  ["sub_type", "Type (Grower/House/Coop)"],
  ["size", "Size"],
  ["country", "Country (ISO-2)"],
  ["region", "Region"],
  ["city", "City"],
  ["postal_code", "Postal code"],
  ["address", "Address"],
  ["lat", "Lat"],
  ["lng", "Lng"],
  ["email", "Email"],
  ["phone", "Phone"],
  ["website", "Website"],
  ["source", "Source (gouv.fr/comite/manual)"],
  ["source_url", "Source URL"],
];

@customElement("company-detail-page")
export class CompanyDetailPage extends LitElement {
  static styles = [
    sharedStyles,
    css`
      .page {
        padding: 24px 32px;
        max-width: 1000px;
        margin: 0 auto;
      }
      .top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        margin-bottom: 6px;
      }
      h1 {
        margin: 0;
        font-size: 24px;
      }
      .grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 14px;
        margin: 18px 0;
      }
      @media (min-width: 600px) {
        .grid {
          grid-template-columns: repeat(2, 1fr);
        }
      }
      .full {
        grid-column: 1 / -1;
      }
      .actions {
        display: flex;
        gap: 10px;
        align-items: center;
      }
      h2 {
        font-size: 18px;
        margin: 26px 0 10px;
      }
      .tags {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
        margin: 8px 0 18px;
      }
      .tag {
        padding: 3px 10px;
        border-radius: 999px;
        font-size: 12px;
        background: var(--md-sys-color-secondary-container);
        color: var(--md-sys-color-on-secondary-container);
      }
      .onboard {
        border: 1px solid var(--md-sys-color-outline-variant);
        background: color-mix(in srgb, var(--md-sys-color-primary) 5%, transparent);
        border-radius: 14px;
        padding: 16px 18px;
        margin: 8px 0 18px;
      }
      .onboard-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
      }
      .onboard-title {
        font-weight: 600;
      }
      .onboard-row {
        display: flex;
        gap: 12px;
        align-items: center;
        flex-wrap: wrap;
      }
      .onboard-row md-outlined-select {
        min-width: 260px;
      }
      .chip {
        display: inline-block;
        padding: 2px 10px;
        border-radius: 999px;
        font-size: 12px;
        font-weight: 600;
        background: var(--md-sys-color-secondary-container);
        color: var(--md-sys-color-on-secondary-container);
      }
      .mono {
        font-family: ui-monospace, monospace;
        font-size: 12px;
      }
      .contact {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 12px;
        border: 1px solid var(--md-sys-color-outline-variant);
        border-radius: 12px;
        margin-bottom: 8px;
      }
      .contact .who {
        font-weight: 600;
      }
      .muted {
        color: var(--md-sys-color-on-surface-variant);
        font-size: 13px;
      }
      .add-contact {
        display: grid;
        grid-template-columns: 1fr;
        gap: 8px;
        margin-top: 10px;
      }
      @media (min-width: 600px) {
        .add-contact {
          grid-template-columns: 1fr 1fr;
        }
      }
      @media (min-width: 840px) {
        .add-contact {
          grid-template-columns: repeat(4, 1fr);
        }
      }
      .center {
        padding: 60px;
        text-align: center;
      }
      .error {
        color: var(--md-sys-color-error);
      }
    `,
  ];

  @property({ type: String }) companyId = "";

  @state() private form: Partial<Company> = {};
  @state() private contacts: Contact[] = [];
  @state() private loading = true;
  @state() private error = "";
  @state() private saving = false;
  @state() private newContact: Partial<Contact> = {};
  @state() private prospect: Prospect | null = null;
  @state() private showOnboard = false;
  @state() private goods: GiGood[] = [];
  @state() private giChoice = "";
  @state() private onboarding = false;

  connectedCallback(): void {
    super.connectedCallback();
    void this.load();
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.error = "";
    try {
      const res = await companies.get(this.companyId);
      this.form = { ...res.company };
      this.contacts = res.contacts;
      this.prospect = await provisioning.prospectByCompany(this.companyId);
    } catch (e) {
      this.error = e instanceof ApiError ? e.message : "Failed to load company";
    } finally {
      this.loading = false;
    }
  }

  private set(key: keyof Company, e: Event): void {
    const v = inputValue(e);
    this.form = { ...this.form, [key]: key === "lat" || key === "lng" ? Number(v) : v };
  }

  private async save(): Promise<void> {
    this.saving = true;
    this.error = "";
    try {
      const c = await companies.update(this.companyId, this.form);
      this.form = { ...c };
      toast("Saved");
    } catch (e) {
      this.error = e instanceof ApiError ? e.message : "Could not save";
    } finally {
      this.saving = false;
    }
  }

  private async deleteCompany(): Promise<void> {
    if (!confirm("Delete this company?")) return;
    try {
      await companies.remove(this.companyId);
      navigate("/companies");
    } catch (e) {
      this.error = e instanceof ApiError ? e.message : "Could not delete";
    }
  }

  private async addContact(): Promise<void> {
    if (!this.newContact.name?.trim()) return;
    try {
      const c = await companies.addContact(this.companyId, {
        ...this.newContact,
        source: "manual",
      });
      this.contacts = [...this.contacts, c];
      this.newContact = {};
    } catch (e) {
      this.error = e instanceof ApiError ? e.message : "Could not add contact";
    }
  }

  private async removeContact(id: string): Promise<void> {
    try {
      await companies.removeContact(id);
      this.contacts = this.contacts.filter((c) => c.id !== id);
    } catch (e) {
      this.error = e instanceof ApiError ? e.message : "Could not remove contact";
    }
  }

  private async openOnboard(): Promise<void> {
    this.showOnboard = true;
    if (this.goods.length === 0) {
      try {
        this.goods = await provisioning.giGoods();
        this.giChoice = this.goods[0]?.id ?? "";
      } catch {
        this.error = "Could not load GI goods.";
      }
    }
  }

  private async startOnboarding(): Promise<void> {
    if (!this.giChoice) return;
    this.onboarding = true;
    this.error = "";
    try {
      const c = this.form;
      const primary = this.contacts[0];
      const p = await provisioning.createProspect({
        giProductId: this.giChoice,
        legalName: c.legal_name || c.name || "",
        siren: c.registry_id || "",
        registeredAddress: [c.address, c.postal_code, c.city].filter(Boolean).join(", "),
        lat: c.lat,
        lng: c.lng,
        contactName: primary?.name || "",
        contactEmail: primary?.email || c.email || "",
        contactPhone: primary?.phone || c.phone || "",
        companyRef: this.companyId,
        source: c.source || "registry",
        notes: c.notes || "",
      });
      navigate(`/provisioning/ebsi/${p.id}`);
    } catch (e) {
      this.error = e instanceof ApiError ? e.message : "Could not start onboarding";
      this.onboarding = false;
    }
  }

  private renderOnboard() {
    if (this.prospect) {
      const p = this.prospect;
      return html`<div class="onboard">
        <div class="onboard-head">
          <div>
            <div class="onboard-title">EBSI onboarding</div>
            <div class="muted">
              <span class="chip">${STAGE_LABEL[p.stage] ?? p.stage}</span>
              ${p.ebsiDid ? html`· <span class="mono">${p.ebsiDid}</span>` : ""}
            </div>
          </div>
          <md-filled-button @click=${() => navigate(`/provisioning/ebsi/${p.id}`)}>
            Open onboarding →
          </md-filled-button>
        </div>
      </div>`;
    }
    if (!this.showOnboard) {
      return html`<div class="onboard">
        <div class="onboard-head">
          <div>
            <div class="onboard-title">EBSI onboarding</div>
            <div class="muted">Convert this producer into a tenant and issue its EBSI identity.</div>
          </div>
          <md-outlined-button @click=${() => void this.openOnboard()}>Start onboarding</md-outlined-button>
        </div>
      </div>`;
    }
    return html`<div class="onboard">
      <div class="onboard-title">Start EBSI onboarding</div>
      <div class="muted" style="margin-bottom:10px">Pick the GI good this producer will be onboarded under.</div>
      ${this.goods.length === 0
        ? html`<p class="muted">No GI goods available.</p>`
        : html`<div class="onboard-row">
            <md-outlined-select
              label="GI good"
              .value=${this.giChoice}
              @change=${(e: Event) => (this.giChoice = (e.target as HTMLSelectElement).value)}
            >
              ${this.goods.map(
                (g) => html`<md-select-option value=${g.id}
                  ><div slot="headline">${g.name}${g.region ? ` — ${g.region}` : ""}</div></md-select-option
                >`,
              )}
            </md-outlined-select>
            <md-filled-button ?disabled=${this.onboarding || !this.giChoice} @click=${() => void this.startOnboarding()}>
              ${this.onboarding ? "Starting…" : "Create & open onboarding"}
            </md-filled-button>
            <md-text-button @click=${() => (this.showOnboard = false)}>Cancel</md-text-button>
          </div>`}
    </div>`;
  }

  render() {
    if (this.loading) {
      return html`<app-shell active="/companies"
        ><div class="center"><md-circular-progress indeterminate></md-circular-progress></div
      ></app-shell>`;
    }
    const c = this.form;
    return html`
      <app-shell active="/companies">
        <div class="page">
          <md-text-button @click=${() => navigate("/companies")}>← Producers</md-text-button>
          <div class="top">
            <h1>${c.name || "Company"}</h1>
            <div class="actions">
              <md-outlined-select
                label="Status"
                .value=${c.status ?? ""}
                @change=${(e: Event) => (this.form = { ...this.form, status: (e.target as HTMLSelectElement).value as Company["status"] })}
              >
                ${STATUSES.map(
                  (s) => html`<md-select-option value=${s}><div slot="headline">${s}</div></md-select-option>`,
                )}
              </md-outlined-select>
              <md-filled-button ?disabled=${this.saving} @click=${() => void this.save()}
                >${this.saving ? "Saving…" : "Save"}</md-filled-button
              >
              <md-text-button @click=${() => void this.deleteCompany()}>Delete</md-text-button>
            </div>
          </div>
          ${this.error ? html`<p class="error">${this.error}</p>` : null}

          ${this.renderOnboard()}

          ${c.certifications?.length || c.languages?.length
            ? html`<div class="tags">
                ${(c.certifications ?? []).map((t) => html`<span class="tag">${t}</span>`)}
                ${(c.languages ?? []).map((t) => html`<span class="tag">🗣 ${t}</span>`)}
              </div>`
            : null}

          <div class="grid">
            ${FIELDS.map(
              ([key, label]) => html`
                <md-outlined-text-field
                  label=${label}
                  .value=${String((c[key] as unknown) ?? "")}
                  @input=${(e: Event) => this.set(key, e)}
                ></md-outlined-text-field>
              `,
            )}
            <md-outlined-text-field
              class="full"
              type="textarea"
              rows="3"
              label="Description"
              .value=${c.description ?? ""}
              @input=${(e: Event) => this.set("description", e)}
            ></md-outlined-text-field>
            <md-outlined-text-field
              class="full"
              type="textarea"
              rows="3"
              label="Notes"
              .value=${c.notes ?? ""}
              @input=${(e: Event) => this.set("notes", e)}
            ></md-outlined-text-field>
          </div>

          <h2>Contacts</h2>
          ${this.contacts.length === 0
            ? html`<p class="muted">No contacts yet.</p>`
            : this.contacts.map(
                (ct) => html`
                  <div class="contact">
                    <div>
                      <div class="who">${ct.name || "(unnamed)"} ${ct.role ? html`<span class="muted">· ${ct.role}</span>` : ""}</div>
                      <div class="muted">
                        ${ct.email || ""} ${ct.phone ? `· ${ct.phone}` : ""}
                        ${ct.source ? html`· <em>${ct.source}</em>` : ""}
                      </div>
                    </div>
                    <md-text-button @click=${() => void this.removeContact(ct.id)}>Remove</md-text-button>
                  </div>
                `,
              )}
          <div class="add-contact">
            <md-outlined-text-field
              label="Name"
              .value=${this.newContact.name ?? ""}
              @input=${(e: Event) => (this.newContact = { ...this.newContact, name: inputValue(e) })}
            ></md-outlined-text-field>
            <md-outlined-text-field
              label="Role"
              .value=${this.newContact.role ?? ""}
              @input=${(e: Event) => (this.newContact = { ...this.newContact, role: inputValue(e) })}
            ></md-outlined-text-field>
            <md-outlined-text-field
              label="Email"
              .value=${this.newContact.email ?? ""}
              @input=${(e: Event) => (this.newContact = { ...this.newContact, email: inputValue(e) })}
            ></md-outlined-text-field>
            <md-outlined-text-field
              label="Phone"
              .value=${this.newContact.phone ?? ""}
              @input=${(e: Event) => (this.newContact = { ...this.newContact, phone: inputValue(e) })}
            ></md-outlined-text-field>
          </div>
          <md-outlined-button style="margin-top:10px" @click=${() => void this.addContact()}
            >Add contact</md-outlined-button
          >
        </div>
      </app-shell>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "company-detail-page": CompanyDetailPage;
  }
}
